import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Spinner,
  Alert,
} from "reactstrap"
import Chart from "react-apexcharts"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import {
  clearErrors,
  getAdminDashboardActiveSessions,
  getAdminDashboardCompanyTypes,
  getAdminDashboardDocsMonthly,
  getAdminDashboardSignups,
  getAdminDashboardSummary,
  setThemeColor,
  getMeDashboardSummary,
  getMeDashboardDailyDocs,
  getMeDashboardDocsMonthly,
  getMeDashboardTopTypes,
  getDocumentsAll,
  getCompaniesAll,
  getDomainsRequest,
  getCompanies,
  getDocuments,
  getMyDocuments,
  getUsersData
} from "store/actions"
import moment from "moment"
import "flatpickr/dist/themes/material_blue.css"
import FlatPickr from "react-flatpickr"
import { DATE_PRESETS } from "helpers/helper_funcations"
import {
  findCustomPageBySlug,
  resolveRole3PortalView,
} from "helpers/companyPortalConfig"
import BureauVeritasPortal from "components/Dashboard/BureauVeritasPortal"
import ColdBlockOverview from "components/Dashboard/ColdBlockOverview"
import ColdBlockContacts from "components/Dashboard/ColdBlockContacts"

const Dashboard = props => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { slug } = useParams()
  const fpRef = useRef(null);
  const themeColor = useSelector(state => state.theme.color)
  const {
    message,
    data,
    loading,
    signups,
    docsMonthly,
    companyTypes,
    activeSessions: docsDaily30,
  } = useSelector(state => state.Dashboard)

  //meta title
  document.title = "Dashboard - ColdBlock Technologies Inc."

  const { user: loggedUser } = useSelector(state => state.Login)
  const isUser = [3, 4].includes(Number(loggedUser?.role))
  const customPages = loggedUser?.company?.customPages

  const role3PortalView = useMemo(() => {
    if (!isUser || !slug) return { mode: "home" }
    return resolveRole3PortalView({ slug, customPages })
  }, [isUser, slug, customPages])

  const customPage = useMemo(() => {
    if (!isUser || !slug) return undefined
    return findCustomPageBySlug(customPages, slug)
  }, [isUser, slug, customPages])

  const customPageHtml =
    role3PortalView.mode === "custom-page"
      ? String(customPage?.html ?? role3PortalView.html ?? "")
      : ""

  const customPageDocumentIds =
    role3PortalView.mode === "custom-page"
      ? (role3PortalView.documentIds || [])
      : []

  useEffect(() => {
    if (!isUser || !slug) return
    if (role3PortalView.mode === "redirect-home") {
      navigate("/home", { replace: true })
    }
  }, [isUser, slug, role3PortalView.mode, navigate])
  const cards = data?.cards
  const [selectedPreset, setSelectedPreset] = useState("");

  const fmt = d => moment(d).format("DD MMM YYYY");
  const YEAR = new Date().getFullYear();

  const { signupsCategories, signupsSeries } = useMemo(() => {
    const raw = Array.isArray(signups) ? signups : signups?.data || []

    const categories = raw.map(it =>
      String(it?.label ?? "").replace(/^\d{4}-W/, "W")
    )

    const dataPoints = raw.map(it => Number(it?.count ?? 0))

    return {
      signupsCategories: categories,
      signupsSeries: [{ name: "Signups", data: dataPoints }],
    }
  }, [signups])

  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

  const { docsCategories, docsSeries } = useMemo(() => {
    const raw = Array.isArray(docsMonthly) ? docsMonthly : docsMonthly?.data || [];

    const base = Object.fromEntries(MONTHS.map(m => [m, 0]));

    const normalize = (label) => {
      if (label == null) return null;
      const s = String(label).trim();
      if (/^\d+$/.test(s)) {
        const idx = Math.max(1, Math.min(12, parseInt(s, 10))) - 1;
        return MONTHS[idx];
      }
      const short = s.slice(0, 3).toLowerCase();
      const idx = MONTHS.findIndex(m => m.toLowerCase() === short);
      return idx >= 0 ? MONTHS[idx] : null;
    };

    for (const it of raw) {
      const m = normalize(it?.label);
      const val = Number(it?.count ?? 0);
      if (m) base[m] = Number.isFinite(val) ? val : 0;
    }

    return {
      docsCategories: MONTHS,
      docsSeries: [{ name: "Docs", data: MONTHS.map(m => base[m]) }],
    };
  }, [docsMonthly])

  const { donutLabels, donutSeries, donutColors } = useMemo(() => {
    const src = Array.isArray(companyTypes?.labels)
      ? companyTypes
      : companyTypes?.data || { labels: [], series: [], colors: [] };

    const labels = (src.labels || []).map(s =>
      s && s.length > 18 ? s.slice(0, 15) + "…" : s
    );

    const series = (src.series || []).map(n => Number(n) || 0);

    const fallback = ["#06427a", "#6C5DD3", "#22C55E", "#E11D48", "#F59E0B", "#0EA5E9"];
    const colorsSrc = Array.isArray(src.colors) ? src.colors : [];
    const norm = (c) => (typeof c === "string" && c.trim()
      ? (c.startsWith("#") ? c.trim() : `#${c.trim()}`)
      : null);

    const colors = series.map((_, i) => norm(colorsSrc[i]) || fallback[i % fallback.length]);

    return { donutLabels: labels, donutSeries: series, donutColors: colors };
  }, [companyTypes]);

  const { dailyDocsCategories, dailyDocsSeries, dailyDocsTotal } = useMemo(() => {
    const raw = Array.isArray(docsDaily30) ? docsDaily30 : docsDaily30?.data || [];

    const categories = raw.map(it => String(it?.label ?? ""));
    const dataPoints = raw.map(it => Number(it?.count ?? 0));

    const total = dataPoints.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);

    return {
      dailyDocsCategories: categories,
      dailyDocsSeries: [{ name: "Documents", data: dataPoints }],
      dailyDocsTotal: total,
    };
  }, [docsDaily30]);

  const { topDownloadsCategories, topDownloadsSeries } = useMemo(() => {
    const rows = data?.charts?.topDownloads?.series || [];
    const cats = rows.map(r =>
      (r?.label?.length > 22 ? r.label.slice(0, 19) + "…" : r?.label) || "—"
    );
    const vals = rows.map(r => Number(r?.value ?? 0));
    return {
      topDownloadsCategories: cats,
      topDownloadsSeries: [{ name: "Downloads", data: vals }],
    };
  }, [data]);

  const { topViewsCategories, topViewsSeries } = useMemo(() => {
    const rows = data?.charts?.topViews?.series || [];
    const cats = rows.map(r =>
      (r?.label?.length > 22 ? r.label.slice(0, 19) + "…" : r?.label) || "—"
    );
    const vals = rows.map(r => Number(r?.value ?? 0));
    return {
      topViewsCategories: cats,
      topViewsSeries: [{ name: "Views", data: vals }],
    };
  }, [data]);

  const handleChange = (e) => {
    dispatch(setThemeColor(e.target.value));
  };

  const [selectDate, setSelectDate] = useState([])
  const dateChange = (dates) => {
    setSelectDate(dates);
    setSelectedPreset(dates?.length ? "custom" : "")
  }

  const range = useMemo(() => {
    if (!Array.isArray(selectDate) || selectDate.length === 0) {
      return { startDate: null, endDate: null }
    }
    const [start, endOrNull] = selectDate
    const startISO = start ? new Date(start).toISOString() : null
    const end = endOrNull || start
    const endClamped = end ? new Date(end) : null
    if (endClamped) endClamped.setHours(23, 59, 59, 999)
    const endISO = endClamped ? endClamped.toISOString() : null
    return { startDate: startISO, endDate: endISO }
  }, [selectDate])

  const hasRange = !!(range.startDate && range.endDate);

  const handleRangeClear = () => {
    setSelectDate([]);
    setSelectedPreset("");
    fpRef.current?.flatpickr?.clear();
    fpRef.current?.flatpickr?.close?.();

    if (isUser) {
      dispatch(getMeDashboardSummary());
      dispatch(getMeDashboardDailyDocs());
      dispatch(getMeDashboardDocsMonthly(2025));
      dispatch(getMeDashboardTopTypes());
    } else {
      dispatch(getAdminDashboardSummary());
      dispatch(getAdminDashboardSignups(12));
      dispatch(getAdminDashboardDocsMonthly(2025));
      dispatch(getAdminDashboardCompanyTypes());
      dispatch(getAdminDashboardActiveSessions(20));
    }
  };


  useEffect(() => {
    document.documentElement.style.setProperty("--scrollbar-color", themeColor)
  }, [themeColor])

  useEffect(() => {
    if (isUser) {
      dispatch(getMyDocuments());
      dispatch(getMeDashboardSummary());
      dispatch(getMeDashboardDailyDocs());
      dispatch(getMeDashboardDocsMonthly(2025));
      dispatch(getMeDashboardTopTypes());
    } else {
      dispatch(getAdminDashboardSummary());
      dispatch(getAdminDashboardSignups(12));
      dispatch(getAdminDashboardDocsMonthly(2025));
      dispatch(getAdminDashboardCompanyTypes());
      dispatch(getAdminDashboardActiveSessions(20));
      dispatch(getDocuments());
      dispatch(getDocumentsAll({ all: true }));
      dispatch(getCompaniesAll({ all: true }));
      dispatch(getDomainsRequest());
      dispatch(getCompanies());
      dispatch(getUsersData());
    }
  }, [dispatch, isUser]);

  useEffect(() => {
    const { startDate, endDate } = range;
    if (!startDate || !endDate) return;

    const common = { startDate, endDate };

    if (isUser) {
      dispatch(getMeDashboardSummary(common));
      dispatch(getMeDashboardDailyDocs(common));
      dispatch(getMeDashboardDocsMonthly(2025, common));
      dispatch(getMeDashboardTopTypes(common));
    } else {
      dispatch(getAdminDashboardSummary(common));
      dispatch(getAdminDashboardSignups(12, common));
      dispatch(getAdminDashboardDocsMonthly(2025, common));
      dispatch(getAdminDashboardCompanyTypes(common));
      dispatch(getAdminDashboardActiveSessions(20, common));
    }
  }, [dispatch, isUser, range.startDate, range.endDate]);

  useEffect(() => {
    const t = setTimeout(() =>
      dispatch(clearErrors()), 8000)
    return () => clearTimeout(t)
  }, [message])

  const applyPreset = (key) => {
    setSelectedPreset(key);
    if (key === "custom") {
      setSelectDate([]);
      setTimeout(() => fpRef.current?.flatpickr?.open(), 0);
      return;
    }
    const end = moment().endOf("day");

    let start;
    switch (key) {
      case "last7":
        start = moment().subtract(6, "days").startOf("day");
        break;
      case "last14":
        start = moment().subtract(13, "days").startOf("day");
        break;
      case "last30":
        start = moment().subtract(29, "days").startOf("day");
        break;
      case "thisMonth":
        start = moment().startOf("month");
        break;
      case "lastMonth":
        start = moment().subtract(1, "month").startOf("month");
        const endLastMonth = moment().subtract(1, "month").endOf("month");
        setSelectDate([start.toDate(), endLastMonth.toDate()]);
        fpRef.current?.flatpickr?.setDate([start.toDate(), endLastMonth.toDate()], false);
        return;
      case "ytd":
        start = moment().startOf("year");
        break;
      case "clear":
      default:
        handleRangeClear();
        return;
    }

    setSelectDate([start.toDate(), end.toDate()]);
    fpRef.current?.flatpickr?.setDate([start.toDate(), end.toDate()], false);
  };

  const presetLabel = useMemo(() => {
    if (selectedPreset === "custom") {
      if (selectDate?.length) {
        const [s, eOrNull] = selectDate;
        const e = eOrNull || s;
        return `Custom: ${fmt(s)} – ${fmt(e)}`;
      }
      return "Custom";
    }
    const found = DATE_PRESETS.find(p => p.key === selectedPreset);
    return found?.label || "";
  }, [selectedPreset, selectDate]);

  return (
    <React.Fragment>
      <div className="page-content">
        {!isUser ?
          <Container fluid>
            {/* Render Breadcrumb */}
            {/* <Breadcrumbs
              title={props.t("Dashboards")}
              breadcrumbItem={props.t("Dashboard")}
            /> */}
            {/* Header: Left title, Right filters (pure Reactstrap/Bootstrap) */}
            <Row className="align-items-center gy-2 mb-3">
              <Col xs="12" lg="6">
                <h2 className="m-0">Dashboard</h2>
              </Col>

              <Col xs="12" lg="6">
                <Row className="g-2 justify-content-lg-end">
                  <Col xs="12" md="6" lg="5" className={`${selectedPreset === "custom" ? "" : "d-none"}`}>
                    <div className="position-relative w-100">
                      <FlatPickr
                        ref={fpRef}
                        className="form-control"
                        style={{ borderRadius: 10 }}
                        placeholder="Select date range"
                        options={{ mode: "range", dateFormat: "d M, Y" }}
                        value={selectDate.length ? selectDate : null}
                        onChange={dateChange}
                      />
                      {hasRange && (
                        <button
                          type="button"
                          aria-label="Clear date range"
                          onClick={handleRangeClear}
                          className="btn-close position-absolute top-50 end-0 translate-middle-y me-2"
                          title="Clear"
                        />
                      )}
                    </div>
                  </Col>

                  <Col xs="12" md="6" lg="5">
                    <select
                      className="form-select"
                      value={selectedPreset || ""}
                      style={{ borderRadius: 10 }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        applyPreset(val);
                      }}
                      aria-label="Quick date range"
                    >
                      <option value="" disabled>
                        {presetLabel || "Select quick range"}
                      </option>
                      {DATE_PRESETS.map(p => (
                        <option key={p.key} value={p.key}>{p.label}</option>
                      ))}
                    </select>
                  </Col>
                </Row>
              </Col>
            </Row>

            {!!message && <Alert color="danger" className="mb-4">
              {String(message)}
            </Alert>}
            {loading ? (
              <div
                className="loading-container"
                style={{
                  width: "100%",
                  height: "60dvh",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Spinner />
              </div>
            ) : (
              <Row>
                <Col lg="12">
                  <Row className="gy-3">
                    {!isUser &&
                      <Col xl="4" md="6">
                        <Card>
                          <CardBody>
                            <CardTitle className="mb-2">Companies</CardTitle>
                            <h2 className="mb-0">{cards?.companies?.total ?? 0}</h2>
                            <small className="text-muted">+{cards?.companies?.newLast7d} this week</small>
                          </CardBody>
                        </Card>
                      </Col>}
                    <Col xl="4" md="6">
                      <Card>
                        <CardBody>
                          <CardTitle className="mb-2">Resources</CardTitle>
                          <h2 className="mb-0">{cards?.documents?.total ?? 0}</h2>
                          <small className="text-muted">+{cards?.documents?.createdThisMonth} this month</small>
                        </CardBody>
                      </Card>
                    </Col>
                    {!isUser &&
                      <Col xl="4" md="6">
                        <Card>
                          <CardBody>
                            <CardTitle className="mb-2">Resources Views</CardTitle>
                            <h2 className="mb-0">{cards?.documents?.viewsTotal ?? 0}</h2>
                            <small className="text-muted">all time </small>
                          </CardBody>
                        </Card>
                      </Col>}
                    {!isUser &&
                      <Col xl="4" md="6">
                        <Card>
                          <CardBody>
                            <CardTitle className="mb-2">Resources Downloads</CardTitle>
                            <h2 className="mb-0">{cards?.documents?.downloadsTotal ?? 0}</h2>
                            <small className="text-muted">all time</small>
                          </CardBody>
                        </Card>
                      </Col>}
                    {!isUser &&
                      <Col xl="4" md="6">
                        <Card>
                          <CardBody>
                            <CardTitle className="mb-2">Users</CardTitle>
                            <h2 className="mb-0">{cards?.users?.total ?? 0}</h2>
                            <small className="text-muted">{cards?.users?.weekGrowthPct}% this week</small>
                          </CardBody>
                        </Card>
                      </Col>}
                    <Col xl="4" md="6">
                      <Card>
                        <CardBody>
                          <CardTitle className="mb-2">Password Resets</CardTitle>
                          <h2 className="mb-0">
                            {isUser
                              ? (cards?.me?.resetPassCountTotal ?? 0)
                              : (cards?.passwordResets?.total ?? 0)}
                          </h2>

                          <small className="text-muted">
                            {isUser
                              ? ((cards?.me?.resetPassCountTotal ?? 0) > 0 ? "all time" : "never reset")
                              : `${cards?.passwordResets?.vsYesterdayPct ?? 0}% vs yesterday`}
                          </small>
                        </CardBody>
                      </Card>
                    </Col>
                    {isUser &&
                      <Col xl="4" md="6">
                        <Card>
                          <CardBody>
                            <CardTitle className="mb-2">Total Logins</CardTitle>
                            <h2 className="mb-0">
                              {cards?.me?.loginCountTotal ?? 0}
                            </h2>

                            <small className="text-muted">
                              {loggedUser?.lastLoginAt ? moment(loggedUser?.lastLoginAt).format("DD MMM YYYY, h:mm A") : "—"} last login
                            </small>
                          </CardBody>
                        </Card>
                      </Col>
                    }
                  </Row>

                  <Row className="mt-3 gy-3">
                    {!isUser &&
                      <Col xl="6">
                        <Card>
                          <CardBody>
                            <CardTitle className="mb-3">Signups (Last 12 weeks)</CardTitle>
                            <Chart
                              type="line"
                              height={300}
                              series={signupsSeries}
                              options={{
                                chart: { toolbar: { show: false }, zoom: { enabled: false } },
                                stroke: { curve: "smooth", width: 3 },
                                dataLabels: { enabled: false },
                                xaxis: { categories: signupsCategories },
                                yaxis: { labels: { formatter: val => Math.round(val) } },
                                grid: { strokeDashArray: 4 },
                                tooltip: {
                                  y: { formatter: val => `${val} users` },
                                },
                                colors: [loggedUser?.company?.primaryColor || "#06427a"],
                                fill: { opacity: 0.85 },
                              }}
                            />
                          </CardBody>
                        </Card>
                      </Col>}

                    <Col xs="12" md="12" lg={isUser ? 12 : 6} xl={isUser ? 12 : 6}>
                      <Card>
                        <CardBody>
                          <CardTitle className="mb-3">Resources Uploaded (Monthly)</CardTitle>
                          <Chart
                            type="bar"
                            height={300}
                            series={docsSeries}
                            options={{
                              chart: { toolbar: { show: false } },
                              plotOptions: { bar: { columnWidth: "45%", borderRadius: 4 } },
                              dataLabels: { enabled: false },
                              xaxis: { categories: docsCategories },
                              grid: { strokeDashArray: 4 },
                              tooltip: { y: { formatter: val => `${val} files` } },
                              colors: [loggedUser?.company?.primaryColor || "#06427a"],
                              fill: { opacity: 0.85 },
                            }}
                          />
                        </CardBody>
                      </Card>
                    </Col>

                    <Col xl="4">
                      <Card>
                        <CardBody style={{ paddingBottom: 36 }}>
                          <CardTitle className="mb-3">
                            {isUser ? "Top File Types" : "Top Companies by Users"}
                          </CardTitle>

                          <Chart
                            type="donut"
                            height={320}
                            series={donutSeries}
                            options={{
                              labels: donutLabels,
                              colors: !isUser ? donutColors : undefined,
                              legend: { position: "bottom" },
                              dataLabels: { enabled: true },
                              tooltip: {
                                y: { formatter: (val) => `${val} ${isUser ? "files" : "users"}` }
                              },
                              plotOptions: {
                                pie: {
                                  donut: {
                                    labels: {
                                      show: true,
                                      total: {
                                        show: true,
                                        label: "Total",
                                        formatter: (w) =>
                                          w.globals.seriesTotals.reduce((a, b) => a + b, 0),
                                      },
                                    },
                                  },
                                },
                              },
                            }}
                          />

                          {(!donutSeries || donutSeries.length === 0) && (
                            <div className="text-muted small mt-2">
                              {isUser ? "No files yet." : "No companies found."}
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    </Col>

                    <Col xl="8">
                      <Card>
                        <CardBody>
                          <CardTitle className="mb-3">
                            Daily Resources (30){typeof dailyDocsTotal === "number" ? ` — ${dailyDocsTotal} total` : ""}
                          </CardTitle>

                          <Chart
                            type="area"
                            height={320}
                            series={dailyDocsSeries}
                            options={{
                              chart: { toolbar: { show: false }, zoom: { enabled: false } },
                              stroke: { curve: "smooth", width: 2 },
                              dataLabels: { enabled: false },
                              xaxis: { categories: dailyDocsCategories },
                              grid: { strokeDashArray: 4 },
                              tooltip: { y: { formatter: val => `${val} files` } },
                              fill: {
                                type: "gradient",
                                gradient: { shadeIntensity: 0.3, opacityFrom: 0.5, opacityTo: 0.1 },
                              },
                              colors: [loggedUser?.company?.primaryColor || "#06427a"],
                              fill: { opacity: 0.85 },
                            }}
                          />

                          {(!dailyDocsSeries?.[0]?.data || dailyDocsSeries[0].data.every(v => !v)) && (
                            <div className="text-muted small mt-2">No Resources in the last 30 days.</div>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                    {!isUser && (
                      <>
                        <Col xs="12" md="12" lg="6">
                          <Card>
                            <CardBody>
                              <CardTitle className="mb-3">Top 10 Resources by Downloads</CardTitle>
                              <Chart
                                type="bar"
                                height={320}
                                series={topDownloadsSeries}
                                options={{
                                  chart: { toolbar: { show: false } },
                                  plotOptions: { bar: { borderRadius: 4, horizontal: false, columnWidth: "50%" } },
                                  dataLabels: { enabled: false },
                                  xaxis: { categories: topDownloadsCategories },
                                  grid: { strokeDashArray: 4 },
                                  tooltip: {
                                    y: { formatter: val => `${val} downloads` },
                                  },
                                  colors: [loggedUser?.company?.primaryColor || "#06427a"],
                                  fill: { opacity: 0.85 },
                                }}
                              />
                              {(!topDownloadsSeries?.[0]?.data?.length ||
                                topDownloadsSeries[0].data.every(v => !v)) && (
                                  <div className="text-muted small mt-2">No download data yet.</div>
                                )}
                            </CardBody>
                          </Card>
                        </Col>

                        <Col xs="12" md="12" lg="6">
                          <Card>
                            <CardBody>
                              <CardTitle className="mb-3">Top 10 Resources by Views</CardTitle>
                              <Chart
                                type="bar"
                                height={320}
                                series={topViewsSeries}
                                options={{
                                  chart: { toolbar: { show: false } },
                                  plotOptions: { bar: { borderRadius: 4, horizontal: false, columnWidth: "50%" } },
                                  dataLabels: { enabled: false },
                                  xaxis: { categories: topViewsCategories },
                                  grid: { strokeDashArray: 4 },
                                  tooltip: {
                                    y: { formatter: val => `${val} views` },
                                  },
                                  colors: [loggedUser?.company?.primaryColor || "#06427a"],
                                  fill: { opacity: 0.85 },
                                }}
                              />
                              {(!topViewsSeries?.[0]?.data?.length ||
                                topViewsSeries[0].data.every(v => !v)) && (
                                  <div className="text-muted small mt-2">No view data yet.</div>
                                )}
                            </CardBody>
                          </Card>
                        </Col>
                      </>
                    )}
                  </Row>
                </Col>
              </Row>
            )}
          </Container> :
          <Container fluid>
            <BureauVeritasPortal
              config={loggedUser?.company?.dashboardConfig}
              companyLogoUrl={loggedUser?.company?.logoUrl}
              companyName={loggedUser?.company?.name}
              portalCustomHtmlEnabled={loggedUser?.company?.portalCustomHtmlEnabled}
              portalCustomHtml={loggedUser?.company?.portalCustomHtml}
              customPageHtml={customPageHtml}
              customPageDocumentIds={customPageDocumentIds}
              customPageSlug={slug}
            />
            {/* <ColdBlockOverview /> 
            <ColdBlockContacts /> */}
          </Container>
        }
       
      </div>
    </React.Fragment>
  )
}

Dashboard.propTypes = {
  t: PropTypes.any,
  getAssessmentsData: PropTypes.func,
}

export default withTranslation()(Dashboard)
